import { cn } from '~/utils/cn';
import { formatDateShort } from '~/utils/contractLabels';
import { formatCentsToBRLCompact } from '~/utils/formatFinance';
import type { Contract, ContractInstallment } from '~/store/contracts/interfaces';
import { isContractSettled } from '~/store/contracts/selectors';

import BankAvatar from '../../atoms/BankAvatar/view';
import { Icon } from '../../atoms/Icon/view';
import ProgressBar from '../../atoms/ProgressBar/view';
import RiskChip from '../../atoms/RiskChip/view';
import { Text } from '../../atoms/Text/view';

interface PropsContractCard {
  contract: Contract;
  nextInstallment?: ContractInstallment;
  onClick?: () => void;
  showRisk?: boolean;
  className?: string;
}

export default function ContractCard({
  contract,
  nextInstallment,
  onClick,
  showRisk = false,
  className,
}: PropsContractCard) {
  const paid = isContractSettled(contract);
  const totalProgress =
    contract.amountTotal > 0 ? contract.amountPaid / contract.amountTotal : 0;
  const progressPct = paid ? 100 : Math.round(totalProgress * 100);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full text-left rounded-medium bg-intense-01 border border-intense-03 p-small shadow-level1 hover:shadow-level2 transition-shadow',
        className,
      )}
    >
      <div className="flex items-start gap-smaller">
        <BankAvatar institution={contract.institution} size="md" />

        <div className="flex-1 mr-smaller min-w-0">
          <Text className="text-paragraphLG color-complement-01" numberOfLines={1}>
            {contract.institution.name}
          </Text>
          <Text
            className="mt-quark text-captionMD color-intense-06"
            numberOfLines={1}
          >
            {`${contract.cropDescription ?? '—'} · #${contract.contractNumber}`}
          </Text>
        </div>

        <div className="flex flex-col items-end shrink-0">
          <Text className="text-paragraphLG color-complement-01">
            {formatCentsToBRLCompact(paid ? contract.amountTotal : contract.amountRemaining)}
          </Text>
          <Text className="text-captionMD color-intense-06">
            {paid ? 'total quitado' : 'restante'}
          </Text>
        </div>
      </div>

      {paid ? (
        <div className="mt-small flex items-center justify-between gap-smaller">
          <span className="inline-flex items-center gap-quark px-smaller py-[2px] rounded-full bg-success-01">
            <Icon
              name="icon-circle-check"
              size={12}
              color="#007831"
              rounded={false}
            />
            <Text className="text-captionLG color-success-06">Quitado</Text>
          </span>
          <Text className="text-captionLG color-success-06 shrink-0">100%</Text>
        </div>
      ) : nextInstallment ? (
        <div className="mt-small flex items-center justify-between gap-smaller">
          <div className="flex items-center gap-quark min-w-0">
            <Icon
              name="icon-calendar"
              size={13}
              color="#606060"
              rounded={false}
              className="shrink-0"
            />
            <Text inline className="text-bodyMD color-complement-01">
              {`Vence ${formatDateShort(nextInstallment.dueDate)}`}
            </Text>
            <Text inline className="text-captionMD color-intense-06">
              {`· ${formatCentsToBRLCompact(nextInstallment.amount)}`}
            </Text>
          </div>
          <Text className="text-captionLG color-primary-01 shrink-0">
            {`${progressPct}%`}
          </Text>
        </div>
      ) : (
        <div className="mt-small flex items-center justify-end">
          <Text className="text-captionLG color-primary-01">
            {`${progressPct}%`}
          </Text>
        </div>
      )}

      <div className="mt-quark">
        <ProgressBar value={progressPct} max={100} trackClassName="h-[4px]" />
      </div>

      {showRisk ? (
        <div className="mt-smaller">
          <RiskChip level={contract.riskLevel} />
        </div>
      ) : null}
    </button>
  );
}
